import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, MapPin, Search, X, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface LocationFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

const STORAGE_KEY = 'qr-location-draft';

export function LocationForm({ contentData, onUpdate }: LocationFormProps) {
  const [latitude, setLatitude] = useState(contentData.latitude || '');
  const [longitude, setLongitude] = useState(contentData.longitude || '');
  const [label, setLabel] = useState(contentData.label || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState('');

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.latitude && !contentData.latitude) setLatitude(parsed.latitude);
        if (parsed.longitude && !contentData.longitude) setLongitude(parsed.longitude);
        if (parsed.label && !contentData.label) setLabel(parsed.label);
      } catch {}
    }
  }, []);

  // Save to localStorage and update parent
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ latitude, longitude, label }));
    onUpdate({ latitude, longitude, label });
  }, [latitude, longitude, label, onUpdate]);

  // Validate coordinates
  const isValidLat = latitude !== '' && !isNaN(parseFloat(latitude)) && 
    parseFloat(latitude) >= -90 && parseFloat(latitude) <= 90;
  const isValidLng = longitude !== '' && !isNaN(parseFloat(longitude)) && 
    parseFloat(longitude) >= -180 && parseFloat(longitude) <= 180;
  const isValid = isValidLat && isValidLng;

  // Get current location
  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toFixed(6));
        setLongitude(position.coords.longitude.toFixed(6));
        setLabel('My Current Location');
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('An unknown error occurred');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  // Search for location (opens Google Maps search)
  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    const encodedQuery = encodeURIComponent(searchQuery);
    window.open(`https://www.google.com/maps/search/${encodedQuery}`, '_blank');
  };

  // Test link - open in Google Maps
  const handleTestLink = () => {
    if (!isValid) return;
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Clear form
  const handleClear = () => {
    setLatitude('');
    setLongitude('');
    setLabel('');
    setSearchQuery('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
        Location
      </h3>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGetCurrentLocation}
          disabled={isLocating}
          className="flex-1 gap-2"
        >
          <Navigation className={cn("h-4 w-4", isLocating && "animate-pulse")} />
          {isLocating ? 'Locating...' : 'Use My Location'}
        </Button>
        {(latitude || longitude || label) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {locationError && (
        <p className="text-sm text-destructive">{locationError}</p>
      )}

      {/* Search (informational) */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">
          Search for a place (opens Google Maps)
        </Label>
        <div className="flex gap-2">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="e.g., Eiffel Tower, Paris"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={handleSearch}
            disabled={!searchQuery.trim()}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Find the location, then copy coordinates from Google Maps URL
        </p>
      </div>

      {/* Coordinates */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="latitude">
            Latitude <span className="text-destructive">*</span>
          </Label>
          <Input
            id="latitude"
            type="text"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
            placeholder="e.g., 48.8584"
            className={cn(
              latitude && !isValidLat && "border-destructive focus-visible:ring-destructive"
            )}
          />
          {latitude && !isValidLat && (
            <p className="text-xs text-destructive">Must be between -90 and 90</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="longitude">
            Longitude <span className="text-destructive">*</span>
          </Label>
          <Input
            id="longitude"
            type="text"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
            placeholder="e.g., 2.2945"
            className={cn(
              longitude && !isValidLng && "border-destructive focus-visible:ring-destructive"
            )}
          />
          {longitude && !isValidLng && (
            <p className="text-xs text-destructive">Must be between -180 and 180</p>
          )}
        </div>
      </div>

      {/* Location Label */}
      <div className="space-y-2">
        <Label htmlFor="label">Location Name (optional)</Label>
        <Input
          id="label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g., Our Office, Meeting Point"
        />
      </div>

      {/* Preview */}
      {isValid && (
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-primary" />
            <span className="font-medium">{label || 'Location'}</span>
          </div>
          <p className="text-xs text-muted-foreground font-mono">
            {latitude}, {longitude}
          </p>
        </div>
      )}

      {/* Test Link Button */}
      <div className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleTestLink}
          disabled={!isValid}
          className="w-full gap-2"
        >
          <MapPin className="h-4 w-4" />
          View on Google Maps
          <ExternalLink className="h-3 w-3 ml-auto" />
        </Button>
      </div>

      {/* Info */}
      <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
        <p className="font-medium mb-1">📍 How it works:</p>
        <p>
          When someone scans this QR code, it will open their maps app and show
          this exact location. Perfect for event venues, business locations, or
          meeting points.
        </p>
      </div>
    </motion.div>
  );
}
